// -- [Video walkthrough section - YouTube, Loom, or direct .mp4/.mkv]

import type { TopicVideoConfig } from '@/types/content'

function parseYouTubeId(url: string): string | null {
  const match =
    url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/) ??
    null
  return match?.[1] ?? null
}

function parseLoomId(url: string): string | null {
  const match = url.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/) ?? null
  return match?.[1] ?? null
}

function getEmbedUrl(video: TopicVideoConfig): string | null {
  switch (video.type) {
    case 'youtube': {
      const id = parseYouTubeId(video.url)
      return id ? `https://www.youtube.com/embed/${id}` : null
    }
    case 'loom': {
      const id = parseLoomId(video.url)
      return id ? `https://www.loom.com/embed/${id}` : null
    }
    case 'direct':
      return video.url
    default:
      return null
  }
}

interface VideoSectionProps {
  video: TopicVideoConfig | null | undefined
}

export function VideoSection({ video }: VideoSectionProps) {
  if (!video?.url) return null

  const embedUrl = video.type === 'direct' ? video.url : getEmbedUrl(video)

  if (!embedUrl) return null

  const title = video.title ?? 'Video walkthrough'

  if (video.type === 'direct') {
    const ext = video.url.split('.').pop()?.toLowerCase() ?? ''
    const isVideo = ['mp4', 'mkv', 'webm', 'ogg'].includes(ext)
    if (!isVideo) return null

    return (
      <section id="video-walkthrough" className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Video walkthrough</h2>
          <p className="text-gray-600 mb-8 max-w-2xl">{title}</p>
          <div className="rounded-xl overflow-hidden shadow-lg aspect-video bg-black">
            <video
              controls
              className="w-full h-full object-contain"
              src={video.url}
              title={title}
              preload="metadata"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="video-walkthrough" className="py-16 px-6 bg-white">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Video walkthrough</h2>
        <p className="text-gray-600 mb-8 max-w-2xl">{title}</p>
        <div className="rounded-xl overflow-hidden shadow-lg aspect-video bg-black">
          <iframe
            src={embedUrl}
            title={title}
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            className="w-full h-full"
          />
        </div>
      </div>
    </section>
  )
}
